<template>
  <StaticPageLayout
    title="About"
    subtitle="What this is, how it works, and why the results are sometimes weird."
    lastUpdated="March 19, 2026"
  >
    <p>
      Game Pivot is a game search tool.
      You type what you want in normal language and it tries to find games that match the meaning of that query, not just the exact words.
    </p>

    <p>
      So instead of searching for something like <code>roguelike deckbuilder pixel art</code>,
      you can type something more human and see what comes back.
    </p>

    <h2>What is this based on?</h2>
    <p>
      Mostly public game metadata.
      Titles, descriptions, tags, genres, categories, review text, release data, and other store-related bits and pieces.
    </p>

    <p>
      Then I turn that into searchable text and embeddings, compare it to your query, and try to rank the results in a way that feels useful.
    </p>

    <h2>So this is just keyword search?</h2>
    <p>
      No.
      Keyword matching still matters, but that is not the main idea.
    </p>

    <p>
      The main idea is to search by meaning.
      If your query and a game's data feel close in meaning, it should rank higher even if the exact words do not line up perfectly.
    </p>

    <h2>How does ranking work?</h2>
    <p>
      Roughly like this:
    </p>

    <ul>
      <li>your query gets turned into a semantic representation</li>
      <li>games have semantic representations too</li>
      <li>the app compares them for similarity</li>
      <li>then it adjusts the ranking using tags, genres, categories, and other signals</li>
      <li>some things also get pushed down if they look like a bad fit</li>
    </ul>

    <p>
      That is the simple version.
      The real version is messier, because search is messy.
    </p>

    <h2>How accurate are the results?</h2>
    <p>
      Good enough to be useful.
      Not good enough to be treated as objective truth.
    </p>

    <p>
      Search like this is not exact.
      There is no single correct answer for a query like
      <code>melancholic exploration game with good atmosphere but not too hard</code>.
      There are only better and worse guesses.
    </p>

    <p>
      Sometimes the result looks smart.
      Sometimes it looks cursed.
      Both are normal.
    </p>

    <h2>Why does it get things wrong?</h2>
    <p>
      Usually because the source data is messy.
    </p>

    <ul>
      <li>some games have bad or empty descriptions</li>
      <li>some tags are misleading or useless</li>
      <li>newer or obscure games often have weaker data</li>
      <li>some queries are too broad, too niche, or just contradictory</li>
    </ul>

    <p>
      So if something obvious is missing, it does not always mean the ranking is broken.
      Sometimes the game just has terrible metadata.
    </p>

    <h2>Why is this random obscure game showing up so high?</h2>
    <p>
      Probably because some part of its text matches your query unusually well.
      Or because its tags happen to line up better than you would expect.
      Or because the more obvious games have metadata that is somehow worse.
    </p>

    <p>
      This is one of the funny parts of semantic search.
      Sometimes it finds a hidden gem.
      Sometimes it finds a completely wrong gem.
    </p>

    <h2>Why are some obvious games missing?</h2>
    <p>
      Usually one of these:
    </p>

    <ul>
      <li>the game data is weak</li>
      <li>the query is more specific than it looks</li>
      <li>the filters are excluding it</li>
      <li>the ranking simply missed it this time</li>
    </ul>

    <p>
      Search is ranking, not mind reading.
    </p>

    <h2>What is Steam mode?</h2>
    <p>
      Steam mode searches inside your own library.
      It does not recommend new games to buy.
    </p>

    <p>
      It exists because a lot of people already own more games than they will ever finish.
      So instead of helping you buy game number 437, it helps you pick something from the pile.
    </p>

    <h2>Does Steam mode know my whole account?</h2>
    <p>
      No magic there either.
      If you link or provide a Steam profile, the app uses that to load the profile and library data needed for that mode.
    </p>

    <h2>Why does this site exist?</h2>
    <p>
      Because normal store search is bad at vague queries, recommendation systems are often too generic, and I wanted something closer to search by vibe.
    </p>

    <p>
      Also because building weird search systems is fun.
    </p>

    <h2>Can I trust this data?</h2>
    <p>
      Trust it the same way you trust any rough search or recommendation system.
      It is useful, but it is still an approximation built on imperfect inputs.
    </p>

    <p>
      If you are using it to find something to play, great.
      If you are using it as the basis for a market thesis worth millions, maybe slow down.
    </p>

    <h2>Support the project</h2>
    <p>
      This is an independent project and it costs money to run.
      Hosting, storage, scraping, search infrastructure, embeddings, and all the boring background stuff are not free.
    </p>

    <p>
      If you like it and want to help keep it alive:
      <a
        href="https://patreon.com/GamePivot?utm_medium=about_page&utm_source=site&utm_campaign=site&utm_content=copyLink"
        target="_blank"
        rel="noopener noreferrer"
      >
        support it on Patreon
      </a>.
    </p>

    <h2>Fine print</h2>
    <p>
      Not affiliated with Valve or Steam.
      Data can be incomplete, stale, wrong, or just strange.
      If the ranking looks haunted, that is part of the charm.
    </p>

    <h2>Contact</h2>
    <p>
      Questions, bug reports, complaints:
      <br />
      <a href="mailto:contact@gamepivot.net">contact@gamepivot.net</a>
    </p>
  </StaticPageLayout>
</template>

<script>
import StaticPageLayout from './StaticPageLayout.vue';

export default {
  name: 'AboutPage',
  components: {
    StaticPageLayout
  }
};
</script>